<template>
  <div class="public-booking">
    <div class="public-booking__blob public-booking__blob--top" />
    <div class="public-booking__blob public-booking__blob--bottom" />

    <v-container class="py-8 public-booking__container">
      <div class="d-flex align-center mb-8">
        <img :src="logo" alt="Логотип" class="public-booking__logo">
      </div>

      <div class="text-center mb-8">
        <h1 class="public-booking__title mb-2">Запись на услуги</h1>
        <div class="text-body-1 grey--text text--darken-1">
          Выберите дату — покажем доступное время экспертов.
        </div>
      </div>

      <template v-if="booking.result">
        <v-sheet rounded="xl" class="pa-8 text-center public-booking__result">
          <v-icon size="48" color="#009E60" class="mb-3">mdi-check-circle-outline</v-icon>
          <div class="text-h6 font-weight-bold mb-2">Заявка принята</div>
          <div class="text-body-2 grey--text text--darken-1">
            Мы отправили подтверждение на указанную почту. Ссылка для отмены записи тоже будет в письме.
          </div>
          <v-btn text color="#483CB9" class="mt-4 text-capitalize" @click="startOver">
            Записаться ещё раз
          </v-btn>
        </v-sheet>
      </template>

      <template v-else>
        <PublicMonthGrid
          :month="currentMonth"
          :selected-date="selectedDate"
          :slot-dates="monthAvailabilityDates"
          class="mb-6"
          @update:month="currentMonth = $event"
          @select-date="onSelectDate"
        />

        <div v-if="loading" class="text-center grey--text py-8">
          Ищем свободное время…
        </div>
        <div v-else-if="searched && !optionsByExpert.length" class="text-center grey--text py-8">
          На эту дату свободного времени нет. Попробуйте выбрать другой день.
        </div>

        <PublicExpertCard
          v-for="group in optionsByExpert"
          :key="group.expert.id"
          :expert="group.expert"
          :services="group.services"
          class="mb-4"
          @select-slot="openBooking"
        />
      </template>
    </v-container>

    <GuestBookingDialog
      v-model="dialog"
      :option="selectedOption"
      :loading="booking.loading"
      :error="booking.error"
      @submit="onSubmitBooking"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import PublicMonthGrid from '@/components/Calendar/Public/PublicMonthGrid.vue';
import PublicExpertCard from '@/components/Calendar/Public/PublicExpertCard.vue';
import GuestBookingDialog from '@/components/Calendar/Public/GuestBookingDialog.vue';

export default {
  name: 'PublicBooking',
  components: { PublicMonthGrid, PublicExpertCard, GuestBookingDialog },
  data() {
    return {
      currentMonth: this.$moment().format('YYYY-MM'),
      selectedDate: this.$moment().format('YYYY-MM-DD'),
      searched: false,
      dialog: false,
      selectedOption: null,
    };
  },
  computed: {
    ...mapGetters('publicCalendar', ['loading', 'optionsByExpert', 'booking', 'monthAvailabilityDates']),
    ...mapGetters('auth', ['isTA']),
    // Тот же паттерн выбора лого по сборке, что и в DashboardLayout.vue — isTA не зависит
    // от сессии (читает VUE_APP_MODE на этапе сборки), доступен и на публичном роуте.
    logo() {
      // eslint-disable-next-line global-require, import/no-dynamic-require
      return require(this.isTA ? '@/assets/images/logoTA_RU.svg' : '@/assets/images/logo.svg');
    },
  },
  watch: {
    currentMonth: {
      immediate: true,
      handler(month) {
        this.loadMonthAvailability(month);
      },
    },
  },
  created() {
    this.search();
  },
  methods: {
    ...mapActions('publicCalendar', [
      'searchAvailability',
      'createAppointment',
      'resetBooking',
      'resetOptions',
      'loadMonthAvailability',
    ]),
    onSelectDate(date) {
      // Прошедшие дни не бронируются — молча игнорируем клик (PublicMonthGrid тоже их не эмитит,
      // но дублируем проверку здесь на случай прямого вызова метода).
      if (date < this.$moment().format('YYYY-MM-DD')) return;
      this.selectedDate = date;
      this.search();
    },
    async search() {
      if (!this.selectedDate) return;
      this.searched = true;
      await this.searchAvailability({ date: this.selectedDate });
    },
    openBooking(option) {
      this.selectedOption = option;
      this.dialog = true;
    },
    async onSubmitBooking(payload) {
      try {
        await this.createAppointment(payload);
        this.dialog = false;
        // Слот мог перестать быть доступен для других гостей — обновляем список и точки на сетке.
        await this.search();
        this.loadMonthAvailability(this.currentMonth);
      } catch {
        // ошибка уже отражена в booking.error, диалог остаётся открытым
      }
    },
    startOver() {
      this.resetBooking();
      this.resetOptions();
      this.searched = false;
      this.selectedOption = null;
      this.search();
    },
  },
};
</script>

<style lang="scss" scoped>
.public-booking {
  position: relative;
  min-height: 100vh;
  background: #F7F6FB;
  overflow: hidden;

  &__container {
    max-width: 880px;
    position: relative;
    z-index: 1;
  }

  &__logo {
    height: 26px;
  }

  &__title {
    font-size: 32px;
    font-weight: 700;
    color: #483CB9;
    letter-spacing: -0.01em;
  }

  &__result {
    box-shadow: 0 8px 24px rgba(72, 60, 185, 0.08);
  }

  // Декоративные размытые пятна фона — только форма/цвет бренда, без картинок,
  // повторяют угловые акценты макета.
  &__blob {
    position: absolute;
    border-radius: 50%;
    background: #483CB9;
    opacity: 0.08;
    filter: blur(60px);
    pointer-events: none;
    z-index: 0;

    &--top {
      width: 420px;
      height: 420px;
      top: -180px;
      right: -140px;
    }

    &--bottom {
      width: 360px;
      height: 360px;
      bottom: -160px;
      left: -140px;
    }
  }
}
</style>
